import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { staffBffService, type FeatureFlagEvaluation } from "../api/staffBff.service";
import { isServiceUnavailableError } from "../api/serviceUnavailable";
import { publicRuntimeConfig } from "../config/publicRuntimeConfig";

/**
 * Feature flags context — provides session-scoped feature-flag evaluations
 * fetched from the staff BFF (`GET /api/feature-flags`).
 *
 * The provider must be mounted INSIDE the auth wrapper (AuthenticatedAppWrapper
 * or DevAuthWrapper) so the L500 token is available when the fetch fires —
 * see App.tsx for the required nesting.
 *
 * LIVE SNAPSHOT — the provider fetches on mount and then keeps the snapshot
 * fresh, because the maintenance banner has to be right for staff who already
 * had the tab open when the window opened, and must stop claiming registration
 * is paused once the window has been closed. The refresh runs two ways: a
 * `REFRESH_INTERVAL_MS` poll, and an immediate refetch whenever the document
 * becomes visible again, so a tab returning to the foreground is correct at
 * once rather than up to a poll interval later. The poll is skipped entirely
 * while the document is hidden so background tabs do not hammer the endpoint.
 * Worst case staleness is therefore one poll interval on a tab that stays in
 * the foreground, and effectively zero on a tab the user has just switched to.
 *
 * Two invariants matter to consumers. `isLoading` describes the INITIAL fetch
 * only and never goes back to true on a refresh — FeatureFlagsGate renders a
 * fullscreen loader off it, so flipping it would blank the whole app shell once
 * a minute. And a FAILED refresh keeps the last good snapshot rather than
 * resetting it: a single transient 502 must not drop every flag and make the
 * app look unlicensed until the next poll. Only the initial fetch fails closed
 * to empty flags, except when the BFF reports the service unavailable: then
 * `flagsUnavailable` is set until a poll succeeds.
 *
 * In `DISABLE_AUTH=true` mode the fetch is skipped entirely — no initial
 * request and no polling; the hook `useFeatureFlags` returns `true` for any
 * flag key.
 */

/**
 * How often the snapshot is re-fetched while the document is visible. One
 * minute is the agreed convergence target for the maintenance banner: short
 * enough that staff notice the window opening and closing during it, long
 * enough that an always-open staff tab costs the BFF one cheap request a
 * minute.
 */
const REFRESH_INTERVAL_MS = 60_000;

export type FlagsUnavailable = { service: string };

export type FeatureFlagsContextValue = {
  flags: Record<string, FeatureFlagEvaluation>;
  isLoading: boolean;
  /**
   * Mirrors the staff-bff feature-flags response's `registrationMaintenanceMode`
   * field: true when website registration/login is in maintenance mode. Resolved
   * server-side from the legal500-scoped "website-maintenance-mode" flag and
   * fails open (false) if unresolved. Plain boolean, no tri-state handling.
   */
  registrationMaintenanceMode: boolean;
  /**
   * Mirrors the staff-bff feature-flags response's `registrationMaintenanceNotice`
   * field: an operator-selected estimated return time such as "We expect to be back
   * within 30 minutes.". Resolved server-side from the legal500-scoped
   * "website-maintenance-notice" flag and fails open ("") when no estimate is set
   * or the flag is unresolved.
   */
  registrationMaintenanceNotice: string;
  flagsUnavailable?: FlagsUnavailable;
  flagsLoadFailed?: boolean;
  retryFlags?: () => void;
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: {},
  isLoading: true,
  registrationMaintenanceMode: false,
  registrationMaintenanceNotice: "",
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const isAuthDisabled = publicRuntimeConfig.authDisabled;
  const [flags, setFlags] = useState<Record<string, FeatureFlagEvaluation>>({});
  const [registrationMaintenanceMode, setRegistrationMaintenanceMode] = useState(false);
  const [registrationMaintenanceNotice, setRegistrationMaintenanceNotice] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(!isAuthDisabled);
  const [flagsUnavailable, setFlagsUnavailable] = useState<FlagsUnavailable | undefined>();
  const [flagsLoadFailed, setFlagsLoadFailed] = useState(false);
  const reloadFlagsRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (isAuthDisabled) {
      return;
    }

    let cancelled = false;
    // Guards against overlapping requests: a slow response plus a
    // visibilitychange landing on a poll tick could otherwise stack fetches.
    let inFlight = false;

    async function loadFlags(isInitial: boolean) {
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        const response = await staffBffService.featureFlags.list();
        if (!cancelled) {
          setFlags(response.flags ?? {});
          setRegistrationMaintenanceMode(response.registrationMaintenanceMode ?? false);
          setRegistrationMaintenanceNotice(response.registrationMaintenanceNotice ?? "");
          setFlagsUnavailable(undefined);
          setFlagsLoadFailed(false);
        }
      } catch (error) {
        if (!cancelled) {
          if (isInitial) {
            console.error("[FeatureFlagsProvider] initial flag fetch failed", error);
            setFlagsLoadFailed(true);
            // Nothing good to keep yet, so fail closed to "no flags".
            setFlags({});
            setRegistrationMaintenanceMode(false);
            setRegistrationMaintenanceNotice("");
            if (isServiceUnavailableError(error)) {
              setFlagsUnavailable({ service: error.service ?? "platform" });
            }
          } else {
            // Keep the previous snapshot: a transient failure must not strip
            // every flag out of a working session.
            console.error(
              "[FeatureFlagsProvider] flag refresh failed, keeping last snapshot",
              error,
            );
          }
        }
      } finally {
        inFlight = false;
        // isLoading tracks the initial fetch only; refreshes leave it alone so
        // the app shell never flickers back to its loading state.
        if (!cancelled && isInitial) {
          setIsLoading(false);
        }
      }
    }

    void loadFlags(true);
    reloadFlagsRef.current = () => void loadFlags(false);

    const interval = setInterval(() => {
      if (document.visibilityState === "hidden") {
        return;
      }
      void loadFlags(false);
    }, REFRESH_INTERVAL_MS);

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void loadFlags(false);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      reloadFlagsRef.current = () => undefined;
    };
  }, [isAuthDisabled]);

  const retryFlags = useCallback(() => reloadFlagsRef.current(), []);

  return (
    <FeatureFlagsContext.Provider
      value={{
        flags,
        isLoading,
        registrationMaintenanceMode,
        registrationMaintenanceNotice,
        flagsUnavailable,
        flagsLoadFailed,
        retryFlags,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}
